import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Wind } from 'lucide-react';
import { useClickSound } from '../../hooks/useClickSound';
interface BreathingStepProps {
  onComplete: () => void;
  onClose?: () => void;
  breathingTechnique?: string;
  breathingName?: string;
  breathingPattern?: number[];
}
interface BreathingPhase {
  name: string;
  label: string;
  duration: number;
}
const TECHNIQUE_DESCRIPTIONS: Record<string, string> = {
  box: 'Wdech → Wstrzymaj → Wydech → Wstrzymaj',
  '478': 'Wdech 4s → Wstrzymaj 7s → Wydech 8s',
  cleansing: 'Miarowy wdech → Dłuższy wydech',
  resonance: 'Płynny wdech → Płynny wydech'
};
const TECHNIQUE_SUBTITLES: Record<string, string> = {
  box: 'Zatrzymuje reakcję stresową i szybko uziemia',
  '478': 'Wymusza relaksację, dotlenia mózg i budzi z letargu',
  cleansing: 'Oczyszcza resztki napięć i przygotowuje umysł',
  resonance: 'Zsynchronizowany oddech wzmacnia intuicję'
};
function buildPhases(pattern: number[]): BreathingPhase[] {
  const phases: BreathingPhase[] = [];
  if (pattern.length === 4) {
    // Box breathing: inhale, hold, exhale, hold
    phases.push({
      name: 'wdech',
      label: 'Wdech...',
      duration: pattern[0]
    });
    if (pattern[1] > 0) phases.push({
      name: 'wstrzymaj',
      label: 'Wstrzymaj...',
      duration: pattern[1]
    });
    phases.push({
      name: 'wydech',
      label: 'Wydech...',
      duration: pattern[2]
    });
    if (pattern[3] > 0) phases.push({
      name: 'wstrzymaj2',
      label: 'Wstrzymaj...',
      duration: pattern[3]
    });
  } else if (pattern.length === 3) {
    // 3-element: inhale, hold (0=skip), exhale
    phases.push({
      name: 'wdech',
      label: 'Wdech...',
      duration: pattern[0]
    });
    if (pattern[1] > 0) phases.push({
      name: 'wstrzymaj',
      label: 'Wstrzymaj...',
      duration: pattern[1]
    });
    phases.push({
      name: 'wydech',
      label: 'Wydech...',
      duration: pattern[2]
    });
  } else if (pattern.length === 2) {
    // Simple: inhale, exhale
    phases.push({
      name: 'wdech',
      label: 'Wdech...',
      duration: pattern[0]
    });
    phases.push({
      name: 'wydech',
      label: 'Wydech...',
      duration: pattern[1]
    });
  }
  return phases;
}
export function BreathingStep({
  onComplete,
  onClose,
  breathingTechnique = 'box',
  breathingName = 'Oddech Pudełkowy',
  breathingPattern = [4, 4, 4, 4]
}: BreathingStepProps) {
  const phases = useMemo(() => buildPhases(breathingPattern), [breathingPattern]);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(phases[0]?.duration || 4);
  const [cycle, setCycle] = useState(1);
  const [isActive, setIsActive] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const {
    playClick,
    playBowl,
    playSuccess
  } = useClickSound();
  const totalCycles = 3;
  const currentPhase = phases[phaseIndex] || phases[0];
  const lastSpokenRef = useRef<string>('');
  // Format the pattern for display, e.g. "4-4-4-4" or "4-7-8"
  const patternDisplay = breathingPattern.filter(n => n > 0).join('-');
  // Web Speech API — speak phase names in Polish
  const SPEECH_MAP: Record<string, string> = {
    wdech: 'Wdech',
    wstrzymaj: 'Wstrzymaj',
    wstrzymaj2: 'Wstrzymaj',
    wydech: 'Wydech'
  };
  const speakPhase = useCallback((phaseName: string) => {
    const text = SPEECH_MAP[phaseName];
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
    // Don't repeat the same word twice in a row
    if (lastSpokenRef.current === text) {
      // Allow repeating for new cycles
    }
    lastSpokenRef.current = text;
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pl-PL';
    utterance.rate = 0.8;
    utterance.pitch = 0.9;
    utterance.volume = 0.7;
    // Try to find a Polish voice
    const voices = window.speechSynthesis.getVoices();
    const plVoice = voices.find(v => v.lang.startsWith('pl'));
    if (plVoice) utterance.voice = plVoice;
    window.speechSynthesis.speak(utterance);
  }, []);
  // Preload voices (some browsers need this)
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (isActive && !isFinished) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Move to next phase
            setPhaseIndex(currentIdx => {
              const nextIdx = currentIdx + 1;
              if (nextIdx >= phases.length) {
                // End of cycle
                if (cycle >= totalCycles) {
                  setIsFinished(true);
                  setIsActive(false);
                  playSuccess();
                  if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
                  return currentIdx;
                }
                setCycle(c => c + 1);
                playBowl();
                speakPhase(phases[0].name);
                // Reset timeLeft for first phase of next cycle
                setTimeout(() => setTimeLeft(phases[0].duration), 0);
                return 0;
              }
              // Play bowl on phase transitions (exhale start, new cycle start)
              if (phases[nextIdx].name === 'wydech' || phases[nextIdx].name === 'wdech') {
                playBowl();
              }
              speakPhase(phases[nextIdx].name);
              setTimeout(() => setTimeLeft(phases[nextIdx].duration), 0);
              return nextIdx;
            });
            return prev; // Will be overwritten by setTimeout above
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isActive, isFinished, cycle, phases, playBowl, playSuccess, speakPhase]);
  const handleStart = () => {
    playClick();
    setIsActive(true);
    setPhaseIndex(0);
    setTimeLeft(phases[0].duration);
    playBowl();
    // Speak the first phase
    speakPhase(phases[0].name);
  };
  const handleComplete = () => {
    playClick();
    onComplete();
  };
  const getOrbScale = () => {
    if (!isActive && !isFinished) return {
      scale: 1,
      opacity: 0.5
    };
    if (isFinished) return {
      scale: 1.5,
      opacity: 0.8
    };
    const phaseName = currentPhase.name;
    if (phaseName === 'wdech') return {
      scale: 2,
      opacity: 0.8
    };
    if (phaseName === 'wydech') return {
      scale: 1,
      opacity: 0.5
    };
    // Hold phases — gentle pulse
    const holdScale = phaseName === 'wstrzymaj' ? 2 : 1;
    return {
      scale: [holdScale, holdScale + 0.05, holdScale],
      opacity: phaseName === 'wstrzymaj' ? 0.8 : 0.5,
      transition: {
        repeat: Infinity,
        duration: 1
      }
    };
  };
  const getTransitionDuration = () => {
    if (!isActive || isFinished) return {
      duration: 1
    };
    if (currentPhase.name === 'wdech' || currentPhase.name === 'wydech') {
      return {
        duration: currentPhase.duration,
        ease: 'easeInOut' as const
      };
    }
    return {
      duration: 1
    };
  };
  return <motion.div className="fixed inset-0 z-50 flex flex-col items-center justify-between w-full h-full overflow-hidden bg-slate-900 text-white p-6" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-794">
      {/* Header */}
      <div className="w-full flex justify-between items-center mt-4 z-10" data-id="element-795">
        <div className="flex items-center space-x-3" data-id="element-796">
          {onClose && <button onClick={() => {
          playClick();
          onClose();
        }} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white border border-white/20 backdrop-blur-md" data-id="element-797">
              ✕
            </button>}
          <div className="text-xs font-bold tracking-widest uppercase opacity-70" data-id="element-798">
            Krok 2: Energia
          </div>
        </div>
        <div className="text-xs font-bold tracking-widest uppercase opacity-70" data-id="element-799">
          Cykl {Math.min(cycle, totalCycles)}/{totalCycles}
        </div>
      </div>

      {/* Technique Info Card */}
      <motion.div className="w-full max-w-xs mt-6 z-10" initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-800">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4" data-id="element-801">
          <div className="flex items-center space-x-2.5 mb-2" data-id="element-802">
            <Wind className="w-4 h-4 text-emerald-400" data-id="element-803" />
            <h3 className="text-sm font-semibold text-white" data-id="element-804">
              {breathingName}
            </h3>
            <span className="text-[10px] font-mono bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-500/30" data-id="element-805">
              {patternDisplay}
            </span>
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed" data-id="element-806">
            {TECHNIQUE_DESCRIPTIONS[breathingTechnique] || TECHNIQUE_DESCRIPTIONS.box}
          </p>
          <p className="text-[10px] text-slate-500 mt-1 italic" data-id="element-807">
            {TECHNIQUE_SUBTITLES[breathingTechnique] || TECHNIQUE_SUBTITLES.box}
          </p>
        </div>
      </motion.div>

      {/* Breathing Orb */}
      <div className="flex-1 flex flex-col items-center justify-center w-full relative" data-id="element-808">
        <motion.div className="absolute w-32 h-32 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-200 blur-md" animate={getOrbScale()} transition={getTransitionDuration()} data-id="element-809" />
        <motion.div className="absolute w-32 h-32 rounded-full bg-gradient-to-tr from-emerald-300 to-teal-100 opacity-50 blur-xl" animate={getOrbScale()} transition={getTransitionDuration()} data-id="element-810" />

        <div className="z-10 text-center mt-48" data-id="element-811">
          <AnimatePresence mode="wait" data-id="element-812">
            <motion.h2 key={isFinished ? 'done' : isActive ? currentPhase.label : 'ready'} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="text-3xl font-light mb-2" data-id="element-813">
              {isFinished ? 'Harmonia osiągnięta' : isActive ? currentPhase.label : 'Gotowy?'}
            </motion.h2>
          </AnimatePresence>
          {isActive && !isFinished && <div className="text-xl font-medium opacity-70 tabular-nums" data-id="element-814">
              {timeLeft}
            </div>}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="mb-10 z-10 w-full max-w-xs flex flex-col items-center space-y-6" data-id="element-815">
        {!isActive && !isFinished ? <button onClick={handleStart} className="w-full bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full py-4 text-sm font-bold hover:bg-white/20 transition-colors" data-id="element-816">
            Rozpocznij {breathingName}
          </button> : isFinished ? <motion.button onClick={handleComplete} className="w-full bg-white text-slate-900 rounded-full py-4 text-sm font-bold hover:bg-slate-100 transition-colors flex items-center justify-center space-x-2" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} data-id="element-817">
            <span data-id="element-818">Przejdź dalej</span>
            <ArrowRight className="w-4 h-4" data-id="element-819" />
          </motion.button> : <div className="flex flex-col items-center" data-id="element-820">
            <div className="h-14" data-id="element-821" />
            <button onClick={handleComplete} className="text-xs text-white/40 hover:text-white/70 transition-colors mt-2" data-id="element-822">
              Pomiń →
            </button>
          </div>}
      </div>
    </motion.div>;
}